import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Redirect as ShopifyRedirect } from '@shopify/app-bridge/actions';
import { useAppBridge } from '@shopify/app-bridge-react';
import { useApp } from '@context/app';
import { useCurrentSession } from '@hooks';
import { ShopifyExternalRoutes } from '@utils/url';

export const useAppBridgeRedirect = () => {
  const shopifyAppBridge = useAppBridge();

  const appBridgeRedirect = async (url: string) => {
    const redirect = ShopifyRedirect.create(shopifyAppBridge as any);
    const redirectUrl = new URL(url);
    redirect.dispatch(
      ShopifyRedirect.Action.ADMIN_PATH,
      url.replace('https://' + redirectUrl.host, ''),
    );
  };

  return { appBridgeRedirect };
};

export const usePreventRouteChange = (
  checkChange: boolean,
  onConfirm: (url: string) => void,
) => {
  const router = useRouter();
  const { setShowSave } = useApp();

  useEffect(() => {
    const routeChangeStart = (url: string) => {
      if (router.asPath !== url) {
        if (checkChange) {
          router.events.emit('routeChangeError');
          onConfirm && onConfirm(url);
          try {
            // ex. /account/subscription -> /performance/overview, navigation
            // link was clicked and needs to be reverted
            if (router.asPath.split('/')[1] !== url.split('/')[1]) {
              router.back();
            }
          } catch (error) {
            console.error(error);
          }
          throw 'Aborting route change';
        } else {
          /* Handle clearing save bar when navigating between tabs on the same page */
          setShowSave && setShowSave(false);
        }
      }
    };

    router.events.on('routeChangeStart', routeChangeStart);

    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
    };
  }, [
    checkChange,
    router.asPath,
    router.events,
    onConfirm,
    router,
    setShowSave,
  ]);
};

export const useExternalShopifyRoutes = () => {
  const session = useCurrentSession();
  return ShopifyExternalRoutes(session?.shop!);
};
